import { ru } from '@/i18n/ru';

import { ApiError, NetworkError } from './client';

export interface FieldError {
  /** The field the server named; empty when it refused the body as a whole. */
  field: string;
  /** Position inside a list field ("phones.1" -> 1), if any. */
  index: number | null;
  /** Pydantic error type or "value_error" for service-level checks. */
  type: string;
  /** What the server said, in its own words. */
  message: string;
}

/** Everything a 422 rejected, including what it rejected about the body as a whole. */
export function serverErrors(error: unknown): FieldError[] {
  if (!(error instanceof ApiError) || error.status !== 422 || !Array.isArray(error.detail)) {
    return [];
  }
  const result: FieldError[] = [];
  for (const item of error.detail as unknown[]) {
    if (typeof item !== 'object' || item === null) continue;
    const { loc, type, msg } = item as { loc?: unknown; type?: unknown; msg?: unknown };
    if (!Array.isArray(loc) || loc[0] !== 'body') continue;
    const [field = '', position] =
      loc.length < 2 ? ['', undefined] : String(loc[1]).split('.');
    const index = position !== undefined && /^\d+$/.test(position) ? Number(position) : null;
    result.push({
      field,
      index,
      type: typeof type === 'string' ? type : 'value_error',
      message: typeof msg === 'string' ? msg : '',
    });
  }
  return result;
}

/** Field errors from a 422 response (loc = ["body", field, ...]). */
export function fieldErrors(error: unknown): FieldError[] {
  return serverErrors(error).filter((item) => item.field !== '');
}

/** What a 422 said that no field on this form can show.
 *
 * A rejection the form drops on the floor is a hole in the form - a field the server
 * requires and the screen does not have, or a rule about the whole body. Saying it out
 * loud, in the server's own words and with the name it used, is what makes such a hole
 * visible instead of silent. */
export function unmappedErrors(error: unknown, known: readonly string[]): string[] {
  return serverErrors(error)
    .filter((item) => item.field === '' || !known.includes(item.field))
    .map((item) =>
      item.field === '' ? item.message : ru.errors.serverField(item.field, item.message),
    )
    .filter((text) => text !== '');
}

/** Machine-readable code of a 409 response (constraint name or state). */
export function conflictCode(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const detail = error.detail as { code?: unknown } | null;
  return detail && typeof detail.code === 'string' ? detail.code : 'conflict';
}

/** Extra facts a 409 carries next to its code (app.api.crud.conflict). */
export function conflictFields(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.status !== 409) return [];
  const detail = error.detail as { fields?: unknown } | null;
  if (!detail || !Array.isArray(detail.fields)) return [];
  return detail.fields.filter((field): field is string => typeof field === 'string');
}

/** What to tell a person about a refused action.
 *
 * Every 409 the API sends carries a code; a code we have a sentence for becomes that
 * sentence, and one we do not is shown as itself. "Conflicts with existing data" was
 * the old answer to both, and it tells the reader nothing and the developer less. */
/** 503 `maintenance`: the vendor has the system read-only (STAGE_6 6.7). */
export function isMaintenance(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 503) return false;
  const detail = error.detail as { code?: unknown } | null;
  return detail?.code === 'maintenance';
}

export function conflictMessage(error: unknown): string | null {
  if (isMaintenance(error)) return ru.errors.maintenance;
  const code = conflictCode(error);
  if (code === null) return null;
  if (code === 'report_incomplete') {
    const fields = conflictFields(error);
    if (fields.length > 0) {
      return ru.report.missing(
        fields.map((field) => ru.report.fieldNames[field] ?? field).join(', '),
      );
    }
  }
  return ru.callErrors[code] ?? ru.errors.refused(code);
}

export function isNetworkError(error: unknown): boolean {
  return error instanceof NetworkError;
}

interface Invalid {
  loc?: unknown;
  type?: unknown;
  ctx?: unknown;
}

const v = ru.errors.validation;

/** Why the server refused one value, in Russian: pydantic's error types, and the codes
 * the services give their own checks (app.services.crud.InvalidValueError). */
function reasonOf(type: string, ctx: Record<string, unknown>): string {
  const number = (key: string) => Number(ctx[key]);
  switch (type) {
    case 'missing':
      return v.missing;
    case 'string_too_short':
      return ru.errors.minLength(number('min_length'));
    case 'string_too_long':
      return ru.errors.maxLength(number('max_length'));
    case 'too_short':
      return v.tooFew(number('min_length'));
    case 'too_long':
      return v.tooMany(number('max_length'));
    case 'greater_than':
      return ru.errors.greaterThan(number('gt'));
    case 'greater_than_equal':
      return ru.errors.min(number('ge'));
    case 'less_than':
      return v.lessThan(number('lt'));
    case 'less_than_equal':
      return ru.errors.max(number('le'));
    case 'receipt_required':
      return v.receiptRequired(number('min_length'));
    default:
      return v.byType[type] ?? ru.errors.invalidValue;
  }
}

/** The field a 422 names, by its Russian label: the last name in its path that has
 * one ("items", 0, "qty" -> the label of "qty"), the field's own name when none has. */
function fieldOf(loc: readonly unknown[]): string | null {
  const names = loc.slice(1).filter((part): part is string => typeof part === 'string');
  if (names.length === 0) return null;
  const labelled = [...names].reverse().find((name) => v.fields[name] !== undefined);
  return labelled ? (v.fields[labelled] ?? labelled) : (names[names.length - 1] ?? null);
}

/** What a 422 refused, said for a person: which field and what is wrong with it. */
export function invalidMessage(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  if (!Array.isArray(error.detail)) return ru.errors.invalidValue;
  const lines = (error.detail as unknown[]).map((item) => {
    const { loc, type, ctx } = (item ?? {}) as Invalid;
    const reason = reasonOf(
      typeof type === 'string' ? type : 'value_error',
      typeof ctx === 'object' && ctx !== null ? (ctx as Record<string, unknown>) : {},
    );
    const field = Array.isArray(loc) ? fieldOf(loc) : null;
    return field ? v.line(field, reason) : reason;
  });
  return [...new Set(lines)].join('; ') || ru.errors.invalidValue;
}

/** What to tell a person about any failed save: a refusal by its code, a rejected
 * value by its field, and the general words only when the network or the server
 * itself failed. */
export function errorText(error: unknown): string {
  return (
    conflictMessage(error) ??
    invalidMessage(error) ??
    (isNetworkError(error) ? ru.errors.network : ru.errors.saveFailed)
  );
}
